import { defineStore } from "pinia";
import type { UnifiedSubscription, BillingInvoice, BillingProduct } from "~/types/billing";

export const useBillingStore = defineStore("billing", () => {
  const { getSubscription, getProducts, getPortalUrl, getInvoices } = useBilling();
  const { formatPriceFromCents } = usePriceFormatting();

  const subscription = ref<UnifiedSubscription | null>(null);
  const products = ref<BillingProduct[]>([]);
  const storeUrl = ref<string>("");
  const invoices = ref<BillingInvoice[]>([]);
  const isLoading = ref(false);
  const needRefresh = ref(false);

  // Static plan metadata for UPricingPlan mapping
  const featuresMap: Record<string, string[]> = {
    "Foundation Tier": [
      "11 active residents",
      "Unlimited staff users",
      "14-day free trial",
      "Observation & Direct work tracking",
    ],
    "Growth Tier": [
      "18 active residents",
      "Unlimited staff users",
      "14-day free trial",
      "Advanced reporting & export",
    ],
    "Horizon Tier": [
      "25 residents",
      "Unlimited staff users",
      "Custom integrations",
      "Dedicated support",
    ],
  };

  const descriptionMap: Record<string, string> = {
    "Foundation Tier": "Up to 11 active residents",
    "Growth Tier": "Up to 18 active residents",
    "Horizon Tier": "Up to 25 active residents",
  };

  const highlightMap: Record<string, boolean> = {
    "Growth Tier": true,
  };

  /** Format price from cents to display string (e.g. £249) */
  function formatPrice(cents: number | null): string {
    if (!cents) return "Custom";
    return formatPriceFromCents(cents, "GBP");
  }

  /** Computed plans formatted for UPricingPlan binding */
  const plans = computed(() =>
    products.value
      .filter((product) => product.attributes.name !== "Default")
      .map((product) => ({
        title: product.attributes.name,
        description:
          descriptionMap[product.attributes.name] || product.attributes.description || "",
        price: formatPrice(product.price),
        billingCycle: product.interval ? `/${product.interval}` : undefined,
        features: featuresMap[product.attributes.name] || [],
        highlight: !!highlightMap[product.attributes.name],
        slug: product.attributes.slug,
        variantId: product.id,
        raw: product,
        scale: product.attributes.name === "Growth Tier",
        badge: product.attributes.name == "Growth Tier" ? "Most popular" : null,
        orientation: "horizontal",
      })),
  );

  async function fetchSubscription() {
    isLoading.value = true;
    try {
      subscription.value = await getSubscription();
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchProducts() {
    isLoading.value = true;
    try {
      const result = await getProducts();
      products.value = result.variants;
      storeUrl.value = result.storeUrl;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchInvoices() {
    isLoading.value = true;
    try {
      invoices.value = await getInvoices();
    } finally {
      isLoading.value = false;
    }
  }

  async function openPortal(): Promise<string> {
    const url = await getPortalUrl();
    return url;
  }

  return {
    subscription,
    products,
    storeUrl,
    invoices,
    isLoading: readonly(isLoading),
    needRefresh,
    plans,
    featuresMap,
    descriptionMap,
    highlightMap,
    formatPrice,
    fetchSubscription,
    fetchProducts,
    fetchInvoices,
    openPortal,
  };
});
